import type { FxRates, LeadSource, LeadTemperature } from '../../lib/leadsApi';

// Visual + label config for lead enums (CLE-191). Kept in one place so the
// board, list and filters stay consistent.

export const TEMPERATURE_OPTIONS: Array<{ value: LeadTemperature; label: string }> = [
  { value: 'HOT', label: 'Fierbinte' },
  { value: 'WARM', label: 'Cald' },
  { value: 'COLD', label: 'Rece' },
];

// Dot color shown on each lead card for its temperature.
export const TEMPERATURE_DOT: Record<LeadTemperature, string> = {
  HOT: 'bg-red-500',
  WARM: 'bg-amber-400',
  COLD: 'bg-sky-400',
};

export const TEMPERATURE_LABEL: Record<LeadTemperature, string> = {
  HOT: 'Fierbinte',
  WARM: 'Cald',
  COLD: 'Rece',
};

export const SOURCE_OPTIONS: Array<{ value: LeadSource; label: string }> = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'AI_ASSISTANT', label: 'Asistent AI' },
  { value: 'REFERRAL', label: 'Recomandare' },
  { value: 'WEBSITE', label: 'Site web' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Telefon' },
  { value: 'SOCIAL_MEDIA', label: 'Rețele sociale' },
  { value: 'EVENT', label: 'Eveniment' },
  { value: 'OTHER', label: 'Altele' },
];

export const SOURCE_LABEL: Record<LeadSource, string> = Object.fromEntries(
  SOURCE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<LeadSource, string>;

/** What to show for a lead's source: the typed one wins over the built-in. */
export function leadSourceText(lead: { source: LeadSource; sourceLabel?: string | null }): string {
  return lead.sourceLabel || SOURCE_LABEL[lead.source] || lead.source;
}

/** Format a monetary value with its ISO currency code (e.g. €1,200). */
export function formatCurrency(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Fall back when the currency code is not recognised by Intl.
    return `${value.toLocaleString()} ${currency}`;
  }
}

/** Currencies the leads page can show every value in. */
export const DISPLAY_CURRENCIES = ['RON', 'EUR'] as const;
export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number];

/** Convert through RON with BNR rates; null when a rate is missing. */
export function convertAmount(
  value: number,
  from: string,
  to: string,
  rates: FxRates | null | undefined,
): number | null {
  if (from === to) return value;
  const fromRon = rates?.ronPer[from];
  const toRon = rates?.ronPer[to];
  if (!fromRon || !toRon) return null;
  return (value * fromRon) / toRon;
}

/**
 * A lead value in the page's display currency. A converted value keeps the
 * original amount and the BNR date in `title`; without a rate it stays as is.
 */
export function formatLeadValue(
  value: number | null | undefined,
  currency: string,
  display: string | undefined,
  rates: FxRates | null | undefined,
): { text: string; title?: string } {
  if (value === null || value === undefined) return { text: '—' };
  if (!display || display === currency) return { text: formatCurrency(value, currency) };
  const converted = convertAmount(value, currency, display, rates);
  if (converted === null) return { text: formatCurrency(value, currency) };
  return {
    text: formatCurrency(converted, display),
    title: `${formatCurrency(value, currency)} · curs BNR ${rates?.date ?? ''}`.trim(),
  };
}

/** True when a due date is in the past (used to flag overdue next-actions). */
export function isOverdue(due: string | null | undefined): boolean {
  if (!due) return false;
  return new Date(due).getTime() < Date.now();
}

/** Short, locale-aware date for due dates on cards/rows. */
export function formatShortDate(date: string | null | undefined): string {
  if (!date) return '';
  return new Date(date).toLocaleDateString('ro-RO', { month: 'short', day: 'numeric' });
}

/** Initials for an assignee avatar fallback. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
